<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useBotsStore } from '@/features/bots/store'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseContainer from '@/components/ui/BaseContainer.vue'
import ConfirmationDialog from '@/components/ui/ConfirmationDialog.vue'
import deleteIcon from '@/assets/icons/ui/delete_icon.svg'
import editIcon from '@/assets/icons/ui/edit_icon.svg'
import copyIcon from '@/assets/icons/ui/copy_icon.svg'
import CoreForm from '@/components/core/form/CoreForm.vue'
import PlusIcon from 'vue-material-design-icons/Plus.vue'
import CoreTextField from '@/components/core/form/fields/CoreTextField.vue'
import CoreTextareaField from '@/components/core/form/fields/CoreTextareaField.vue'
import CoreSliderField from '@/components/core/form/fields/CoreSliderField.vue'
import { required } from '@/components/core/form/rules'
import type { BotCreateData } from '@/api/contract'
import type { Bot } from '@/types/domain'

const emit = defineEmits<{
  // Lets the host menu deepen its whole-surface warmth (--drill) while a bot
  // edit/import page is open on top of the list.
  (event: 'form-open-change', open: boolean): void
}>()

const botsStore = useBotsStore()
const props = withDefaults(defineProps<{
  activeBotId?: number | null
  selectConversationBot: (botId: number | null) => Promise<unknown>
  disabled?: boolean
}>(), {activeBotId: null, disabled: false})

interface BotFormState {
  name: string
  model: string
  access_key: string
  access_path: string
  context_size: number
  max_tokens: number
  temperature: number
  advanced_prompt: string
}

type BotLike = Bot | null
type ThirdPartyBotSettings = Record<string, unknown> & {
  apiKey?: unknown
  apiUrl?: unknown
  jailbreakPrompt?: unknown
  model?: unknown
  name?: unknown
}

const showBotForm = ref<boolean>(false)
const showImportForm = ref<boolean>(false)
const editingBot = ref<BotLike>(null)
const showDeleteDialog = ref<boolean>(false)
const botToDelete = ref<BotLike>(null)
const isBotsLoadingVisible = ref<boolean>(false)
const optimisticActiveBotId = ref<number | null>(null)
const selectingBot = ref(false)
const selectionError = ref('')
const importJson = ref<string>('')
const importError = ref<string>('')
const BOTS_LOADING_MIN_VISIBLE_MS = 300
let botsLoadingShownAt = 0
let botsLoadingHideTimer: ReturnType<typeof setTimeout> | null = null
let lockedScrollTop = 0
let previousBodyStyle: {
  overflow: string
  position: string
  top: string
  left: string
  right: string
  width: string
} = {
  overflow: '',
  position: '',
  top: '',
  left: '',
  right: '',
  width: '',
}
let previousHtmlOverflow = ''

const botForm = ref<BotFormState>({
  name: '',
  model: '',
  access_key: '',
  access_path: '',
  context_size: 10000,
  max_tokens: 1000,
  temperature: 0.7,
  advanced_prompt: '',
})

const isOpenRouterForm = computed<boolean>(() => Boolean(editingBot.value?.is_openrouter))

const formatTemperature = (value: number): string => value.toFixed(1)
const formatMaxTokens = (value: number): string => value === 0 ? 'Unlimited' : String(value)

const userBots = computed<Bot[]>(() => botsStore.getUserBots)
const hasBots = computed<boolean>(() => userBots.value.length > 0)
// The conversation's active bot floats to the top — this menu is invoked to see
// and change "who am I talking to," so that bot is the natural anchor.
const orderedBots = computed<Bot[]>(() => {
  const activeId = effectiveActiveBotId.value
  if (activeId === null) return userBots.value
  const active = userBots.value.filter(bot => bot.id === activeId)
  const rest = userBots.value.filter(bot => bot.id !== activeId)
  return [...active, ...rest]
})
const conversationBotId = computed(() => props.activeBotId)
const activeBot = computed(() => props.activeBotId != null
  ? botsStore.getBotById(props.activeBotId) ?? null
  : null)
const effectiveActiveBotId = computed<number | null>(() => optimisticActiveBotId.value ?? activeBot.value?.id ?? null)
const isPanelInteractionDisabled = computed<boolean>(() => botsStore.loading || props.disabled || selectingBot.value)
const showInitialBotsLoading = computed<boolean>(() => botsStore.loading && !hasBots.value)
const showBotsLoadingIndicator = computed<boolean>(() => isBotsLoadingVisible.value && hasBots.value)
const isFormOverlayOpen = computed<boolean>(() => showBotForm.value || showImportForm.value)
const botTitle = (bot: BotLike): string => {
  if (bot?.is_openrouter) {
    return bot.name || 'In-house bot'
  }
  return bot?.name || bot?.model || 'Unnamed bot'
}

const deleteMessage = computed<string>(() => {
  if (botToDelete.value) {
    const label = botTitle(botToDelete.value)
    return `Are you sure you want to delete the bot configuration for "${label}"? This action cannot be undone.`
  }
  return 'Are you sure you want to delete this bot configuration?'
})

const openBotForm = (bot: BotLike = null): void => {
  if (isPanelInteractionDisabled.value) return
  if (bot) {
    editingBot.value = bot
    botForm.value = {
      name: bot.name || '',
      model: bot.model || '',
      access_key: bot.access_key || '',
      access_path: bot.access_path || '',
      context_size: bot.context_size ?? 10000,
      max_tokens: bot.max_tokens ?? 1000,
      temperature: bot.temperature ?? 0.7,
      advanced_prompt: bot.advanced_prompt || '',
    }
  } else {
    editingBot.value = null
    botForm.value = {
      name: '',
      model: '',
      access_key: '',
      access_path: '',
      context_size: 10000,
      max_tokens: 1000,
      temperature: 0.7,
      advanced_prompt: '',
    }
  }
  showBotForm.value = true
}

const closeBotForm = (): void => {
  showBotForm.value = false
  editingBot.value = null
  botForm.value = {
    name: '',
    model: '',
    access_key: '',
    access_path: '',
    context_size: 10000,
    max_tokens: 1000,
    temperature: 0.7,
    advanced_prompt: '',
  }
}

const openImportForm = (): void => {
  if (isPanelInteractionDisabled.value) return
  importJson.value = ''
  importError.value = ''
  showImportForm.value = true
}

const closeImportForm = (): void => {
  showImportForm.value = false
  importJson.value = ''
  importError.value = ''
}

const requireImportedString = (settings: ThirdPartyBotSettings, key: keyof ThirdPartyBotSettings): string => {
  const value = settings[key]
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Imported bot JSON is missing "${String(key)}".`)
  }
  return value.trim()
}

const parseImportedBotSettings = (rawJson: string): BotCreateData => {
  let parsed: unknown
  try {
    parsed = JSON.parse(rawJson)
  } catch {
    throw new Error('Imported bot settings must be valid JSON.')
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Imported bot settings must be a JSON object.')
  }

  const settings = parsed as ThirdPartyBotSettings
  const advancedPrompt = typeof settings.jailbreakPrompt === 'string' ? settings.jailbreakPrompt : ''

  return {
    name: requireImportedString(settings, 'name'),
    model: requireImportedString(settings, 'model'),
    access_key: requireImportedString(settings, 'apiKey'),
    access_path: requireImportedString(settings, 'apiUrl'),
    context_size: 10000,
    max_tokens: 1000,
    temperature: 0.7,
    advanced_prompt: advancedPrompt,
    is_default: false,
  }
}

const importBotSettings = async (): Promise<void> => {
  importError.value = ''

  let payload: BotCreateData
  try {
    payload = parseImportedBotSettings(importJson.value)
  } catch (error: unknown) {
    importError.value = error instanceof Error ? error.message : 'Failed to parse imported bot settings.'
    return
  }

  try {
    const createdBot = await botsStore.createBot(payload)
    if (createdBot) {
      await props.selectConversationBot(createdBot.id)
    }
    closeImportForm()
  } catch (error: unknown) {
    console.error('Failed to import bot:', error)
    importError.value = 'Failed to import bot. Please try again.'
  }
}

const saveBotForm = async (): Promise<void> => {
  try {
    if (editingBot.value) {
      const payload: Partial<BotFormState> = isOpenRouterForm.value
        ? {
            name: botForm.value.name || '',
            context_size: botForm.value.context_size,
            max_tokens: botForm.value.max_tokens,
            temperature: botForm.value.temperature,
            advanced_prompt: botForm.value.advanced_prompt || '',
          }
        : { ...botForm.value }
      await botsStore.updateBot(editingBot.value.id, payload)
    } else {
      const createdBot = await botsStore.createBot({
        ...botForm.value,
        is_default: false,
      })
      if (createdBot) {
        await props.selectConversationBot(createdBot.id)
      }
    }
    closeBotForm()
  } catch (error: unknown) {
    console.error('Failed to save bot:', error)
    alert('Failed to save bot. Please try again.')
  }
}

const openDeleteDialog = (bot: BotLike): void => {
  if (isPanelInteractionDisabled.value) return
  botToDelete.value = bot
  showDeleteDialog.value = true
}

const closeDeleteDialog = (): void => {
  showDeleteDialog.value = false
  botToDelete.value = null
}

const selectBot = async (bot: BotLike): Promise<void> => {
  if (!bot || isPanelInteractionDisabled.value) return
  selectingBot.value = true
  selectionError.value = ''
  optimisticActiveBotId.value = bot.id

  try {
    await props.selectConversationBot(bot.id)
  } catch (error: unknown) {
    console.error('Failed to set conversation bot:', error)
    selectionError.value = 'The bot could not be changed. Please try again.'
  } finally {
    optimisticActiveBotId.value = null
    selectingBot.value = false
  }
}

const confirmDeleteBot = async (): Promise<void> => {
  if (!botToDelete.value) return
  const deletedId = botToDelete.value.id

  try {
    await botsStore.deleteBot(deletedId)
    if (conversationBotId.value === deletedId) {
      await props.selectConversationBot(null)
    }
    closeDeleteDialog()
    // The bot's edit form (if this delete was launched from it) now points at a
    // deleted record — close it.
    closeBotForm()
  } catch (error: unknown) {
    console.error('Failed to delete bot:', error)
    alert('Failed to delete bot. Please try again.')
    closeDeleteDialog()
  }
}

const duplicateBot = async (bot: BotLike): Promise<boolean> => {
  if (!bot || bot.is_openrouter || isPanelInteractionDisabled.value) return false
  const baseName = bot.name && bot.name.trim() ? bot.name : (bot.model || 'Bot')
  const duplicatedBot = {
    name: `${baseName} (copy)`,
    model: bot.model || '',
    access_key: bot.access_key || '',
    access_path: bot.access_path || '',
    context_size: bot.context_size,
    max_tokens: bot.max_tokens,
    temperature: bot.temperature,
    advanced_prompt: bot.advanced_prompt || '',
  }

  try {
    await botsStore.createBot({
      ...duplicatedBot,
      is_default: false,
    })
    return true
  } catch (error: unknown) {
    console.error('Failed to duplicate bot:', error)
    alert('Failed to duplicate bot. Please try again.')
    return false
  }
}

// From inside the edit form the duplicate/delete targets are always the bot
// being edited. Duplicate closes the form on success so the new copy is visible
// in the list; delete defers to the confirmation dialog (which closes the form).
const duplicateEditingBot = async (): Promise<void> => {
  const target = editingBot.value
  if (!target) return
  const ok = await duplicateBot(target)
  if (ok) closeBotForm()
}

const requestDeleteEditingBot = (): void => {
  if (!editingBot.value) return
  openDeleteDialog(editingBot.value)
}

const isActiveBot = (bot: BotLike): boolean => effectiveActiveBotId.value === bot?.id
const isDefaultBot = (bot: BotLike): boolean => Boolean(bot?.is_default)

const clearBotsLoadingHideTimer = (): void => {
  if (botsLoadingHideTimer) {
    clearTimeout(botsLoadingHideTimer)
    botsLoadingHideTimer = null
  }
}

const lockDocumentScroll = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const body = document.body
  const html = document.documentElement
  if (body.dataset.botConfigScrollLocked === 'true') return

  lockedScrollTop = window.scrollY || window.pageYOffset || 0
  previousBodyStyle = {
    overflow: body.style.overflow,
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
  }
  previousHtmlOverflow = html.style.overflow

  body.style.overflow = 'hidden'
  body.style.position = 'fixed'
  body.style.top = `-${lockedScrollTop}px`
  body.style.left = '0'
  body.style.right = '0'
  body.style.width = '100%'
  html.style.overflow = 'hidden'
  body.dataset.botConfigScrollLocked = 'true'
}

const unlockDocumentScroll = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const body = document.body
  const html = document.documentElement
  if (body.dataset.botConfigScrollLocked !== 'true') return

  body.style.overflow = previousBodyStyle.overflow
  body.style.position = previousBodyStyle.position
  body.style.top = previousBodyStyle.top
  body.style.left = previousBodyStyle.left
  body.style.right = previousBodyStyle.right
  body.style.width = previousBodyStyle.width
  html.style.overflow = previousHtmlOverflow
  delete body.dataset.botConfigScrollLocked
  window.scrollTo(0, lockedScrollTop)
}

watch(
  [() => botsStore.loading, hasBots],
  ([loading, hasAnyBots]: [boolean, boolean]) => {
    clearBotsLoadingHideTimer()

    if (loading && hasAnyBots) {
      if (!isBotsLoadingVisible.value) {
        isBotsLoadingVisible.value = true
        botsLoadingShownAt = Date.now()
      }
      return
    }

    if (!loading && isBotsLoadingVisible.value) {
      const elapsedMs = Date.now() - botsLoadingShownAt
      const remainingMs = Math.max(0, BOTS_LOADING_MIN_VISIBLE_MS - elapsedMs)
      botsLoadingHideTimer = setTimeout(() => {
        isBotsLoadingVisible.value = false
        botsLoadingHideTimer = null
      }, remainingMs)
      return
    }

    if (!hasAnyBots) {
      isBotsLoadingVisible.value = false
      botsLoadingShownAt = 0
    }
  },
  { immediate: true }
)

watch(
  () => activeBot.value?.id,
  (activeId: number | null | undefined) => {
    if (optimisticActiveBotId.value !== null && activeId === optimisticActiveBotId.value) {
      optimisticActiveBotId.value = null
    }
  }
)

watch(
  isFormOverlayOpen,
  (isOpen: boolean) => {
    emit('form-open-change', isOpen)
    if (isOpen) {
      lockDocumentScroll()
      return
    }
    unlockDocumentScroll()
  }
)

onMounted(async () => {
  if (botsStore.bots.length === 0) {
    try {
      await botsStore.fetchBots()
    } catch (error: unknown) {
      console.error('Failed to fetch bots:', error)
    }
  }
})

onBeforeUnmount(() => {
  clearBotsLoadingHideTimer()
  unlockDocumentScroll()
})
</script>

<template>
  <div class="action-menu-bot-config py-2">
    <div class="action-menu-bot-config__header">
      <span class="m-eyebrow">Configurations</span>
      <button
        type="button"
        class="m-btn m-btn--ghost"
        :disabled="isPanelInteractionDisabled"
        @click="openImportForm"
      >
        <PlusIcon :size="14" />
        Import JSON
      </button>
    </div>

    <div v-if="showInitialBotsLoading" class="action-menu-bot-config__empty">
      <div class="action-menu-bot-config__title">Loading bots...</div>
    </div>

    <div v-else-if="userBots.length === 0" class="action-menu-bot-config__empty">
      <div class="action-menu-bot-config__title">No bots configured yet</div>
      <button
        type="button"
        class="m-btn m-btn--block"
        :disabled="isPanelInteractionDisabled"
        @click="openBotForm()"
      >
        Add your first bot
      </button>
    </div>

    <div v-else class="action-menu-bot-config__list-wrapper">
      <div
        v-if="showBotsLoadingIndicator"
        class="action-menu-bot-config__loading-overlay"
        aria-hidden="true"
      >
        <div class="action-menu-bot-config__loading-pill">Refreshing bots...</div>
      </div>
      <div class="action-menu-bot-config__list">
      <div
        v-for="bot in orderedBots"
        :key="bot.id"
        class="action-menu-bot-config__bot"
        :class="{ 'action-menu-bot-config__bot--active': isActiveBot(bot) }"
      >
        <div class="action-menu-bot-config__bot-row">
          <button
            type="button"
            class="action-menu-bot-config__bot-select"
            :disabled="isPanelInteractionDisabled"
            :class="{ 'action-menu-bot-config__bot-select--disabled': isPanelInteractionDisabled }"
            @click="selectBot(bot)"
          >
            <div class="action-menu-bot-config__bot-title">
              {{ botTitle(bot) }}
            </div>
            <div class="action-menu-bot-config__bot-chips">
              <span
                v-if="isActiveBot(bot)"
                class="m-eyebrow action-menu-bot-config__chip--active"
              >
                Active
              </span>
              <span
                v-if="isDefaultBot(bot)"
                class="m-eyebrow"
              >
                Default
              </span>
            </div>
            <div class="action-menu-bot-config__bot-subtitle">
              <span v-if="bot.is_openrouter">In-house bot</span>
              <span v-else>{{ bot.model || 'Unknown' }}</span>
            </div>
          </button>
          <div class="action-menu-bot-config__bot-actions">
            <BaseButton
              variant="icon"
              size="icon"
              color="on-surface"
              :icon="editIcon"
              :disabled="isPanelInteractionDisabled"
              @click.stop="openBotForm(bot)"
              aria-label="Edit bot"
            />
          </div>
        </div>
      </div>
      </div>
    </div>
    <p v-if="selectionError" class="action-menu-bot-config__selection-error" role="alert">{{ selectionError }}</p>

    <button
      type="button"
      class="m-btn m-btn--primary m-btn--block action-menu-bot-config__add-button"
      :disabled="isPanelInteractionDisabled"
      @click="openBotForm()"
    >
      Add bot
    </button>
  </div>

  <div
    v-if="showImportForm"
    class="action-menu-bot-config__form-overlay"
    @click="closeImportForm"
  >
    <BaseContainer
      :show-header="true"
      :show-actions="true"
      padding="p-6"
      background="bg-transparent"
      rounded="rounded-ui"
      shadow="shadow-floating"
      class="action-menu-bot-config__form manuscript-surface w-full max-w-2xl"
      @click.stop
    >
      <template #header>
        <h2 class="action-menu-bot-config__form-title">
          Import Bot Settings
        </h2>
      </template>

      <template #actions>
        <button type="button" class="m-btn m-btn--primary" @click="importBotSettings">
          Import
        </button>
        <button type="button" class="m-btn" @click="closeImportForm">
          Cancel
        </button>
      </template>

      <template #content>
        <div class="p-5">
          <CoreTextareaField
            v-model="importJson"
            name="bot-settings-json"
            label="Bot settings JSON"
            placeholder='{"apiKey":"...","apiUrl":"https://...","model":"...","name":"..."}'
            :rows="10"
            hide-details="never"
          />
          <div
            v-if="importError"
            class="action-menu-bot-config__import-error"
            role="alert"
          >
            {{ importError }}
          </div>
        </div>
      </template>
    </BaseContainer>
  </div>

  <div
    v-if="showBotForm"
    class="action-menu-bot-config__form-overlay"
    @click="closeBotForm"
  >
    <BaseContainer
      :show-header="true"
      :show-actions="true"
      padding="p-6"
      background="bg-transparent"
      rounded="rounded-ui"
      shadow="shadow-floating"
      class="action-menu-bot-config__form manuscript-surface w-full max-w-2xl"
      @click.stop
    >
      <template #header>
        <div class="action-menu-bot-config__form-header">
          <h2 class="action-menu-bot-config__form-title">
            {{ editingBot ? 'Edit Bot' : 'Create Bot' }}
          </h2>
          <div v-if="editingBot" class="action-menu-bot-config__form-manage">
            <BaseButton
              v-if="!isOpenRouterForm"
              variant="icon"
              size="icon"
              color="on-surface"
              :icon="copyIcon"
              :disabled="isPanelInteractionDisabled"
              @click="duplicateEditingBot"
              aria-label="Duplicate bot"
            />
            <BaseButton
              variant="icon"
              size="icon"
              color="danger"
              :icon="deleteIcon"
              :disabled="isPanelInteractionDisabled"
              @click="requestDeleteEditingBot"
              aria-label="Delete bot"
            />
          </div>
        </div>
      </template>

      <template #actions>
        <button type="button" class="m-btn m-btn--primary" @click="saveBotForm">
          {{ editingBot ? 'Update' : 'Create' }}
        </button>
        <button type="button" class="m-btn" @click="closeBotForm">
          Cancel
        </button>
      </template>

      <template #content>
        <div class="p-5">
          <CoreForm class="space-y-4" @submit="saveBotForm">
            <CoreTextField
              v-model="botForm.name"
              name="name"
              label="Bot Name"
              :disabled="isOpenRouterForm"
            />

            <template v-if="!isOpenRouterForm">
              <CoreTextField
                v-model="botForm.model"
                name="model"
                label="Model"
                placeholder="Model identifier"
                :rules="[required]"
                required
              />
              <CoreTextField
                v-model="botForm.access_key"
                name="access_key"
                label="Access Key"
                :rules="[required]"
                required
              />
              <CoreTextField
                v-model="botForm.access_path"
                name="access_path"
                label="Access Path"
                placeholder="API endpoint URL"
                :rules="[required]"
                required
              />
            </template>

            <CoreSliderField
              v-model="botForm.context_size"
              name="context_size"
              label="Context Size"
              :min="1024"
              :max="128000"
              :step="1024"
              :show-value="true"
            />

            <CoreSliderField
              v-model="botForm.max_tokens"
              name="max_tokens"
              label="Max Tokens"
              :min="0"
              :max="1000"
              :step="1"
              :show-value="true"
              :format-value="formatMaxTokens"
            />

            <CoreSliderField
              v-model="botForm.temperature"
              name="temperature"
              label="Temperature"
              :min="0"
              :max="2"
              :step="0.1"
              :format-value="formatTemperature"
            />

            <CoreTextareaField
              v-model="botForm.advanced_prompt"
              name="advanced_prompt"
              label="Advanced Prompt"
              :rows="4"
            />
          </CoreForm>
        </div>
      </template>
    </BaseContainer>
  </div>

  <ConfirmationDialog
    class="action-menu-bot-config__confirmation"
    :show="showDeleteDialog"
    title="Delete Bot Configuration"
    :message="deleteMessage"
    confirm-text="Delete"
    cancel-text="Cancel"
    variant="danger"
    @confirm="confirmDeleteBot"
    @cancel="closeDeleteDialog"
  />
</template>

<style scoped>
.action-menu-bot-config {
  color: var(--manuscript-ink);
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0 14px;
}

.action-menu-bot-config__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 4px 18px 2px;
}

.action-menu-bot-config__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 18px 4px;
  position: relative;
}

.action-menu-bot-config__list-wrapper {
  position: relative;
}

.action-menu-bot-config__loading-overlay {
  position: absolute;
  inset: 0 18px 4px;
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: color-mix(in srgb, var(--manuscript-page) 55%, transparent);
  border-radius: 12px;
  pointer-events: auto;
}

.action-menu-bot-config__loading-pill {
  margin-top: 8px;
  padding: 4px 12px;
  font-size: 12px;
  color: var(--manuscript-muted);
  border: 1px solid var(--manuscript-page-line);
  border-radius: 999px;
  background: color-mix(in srgb, var(--manuscript-page) 92%, black 8%);
  backdrop-filter: blur(3px);
}

/* Bot card: a leaf of warm stone. Quiet at rest; the active bot carries a warm
   left-edge tick (state, not interaction) so it reads without a focus-bloom. */
.action-menu-bot-config__bot {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid var(--manuscript-page-line);
  border-radius: 12px;
  background: color-mix(in srgb, var(--manuscript-ink) 3%, transparent);
  transition: background-color 160ms ease, border-color 160ms ease;
}

.action-menu-bot-config__bot:hover {
  background: color-mix(in srgb, var(--manuscript-ink) 6%, transparent);
}

.action-menu-bot-config__bot--active {
  border-color: color-mix(in srgb, var(--theme-primary) 34%, var(--manuscript-page-line));
  box-shadow: inset 3px 0 0 color-mix(in srgb, var(--theme-primary) 62%, transparent);
}

.action-menu-bot-config__empty {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 18px;
}

.action-menu-bot-config__title {
  font: 400 1rem/1.3 var(--manuscript-serif);
  color: var(--manuscript-ink);
}

.action-menu-bot-config__subtitle {
  font-size: 12px;
  color: var(--manuscript-muted);
}

.action-menu-bot-config__bot-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.action-menu-bot-config__bot-select {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.action-menu-bot-config__bot-select--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.action-menu-bot-config__bot-select:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--theme-primary) 55%, transparent);
  outline-offset: 3px;
  border-radius: 8px;
}

.action-menu-bot-config__bot-title {
  font: 400 1.15rem/1.25 var(--manuscript-serif);
  color: var(--manuscript-ink);
}

.action-menu-bot-config__bot-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
}

/* Chips are etched labels (via .m-eyebrow); the active one warms toward primary. */
.action-menu-bot-config__chip--active {
  color: color-mix(in srgb, var(--manuscript-faint) 40%, var(--theme-primary) 60%);
}

.action-menu-bot-config__bot-subtitle {
  font-size: 12px;
  color: var(--manuscript-muted);
  margin-top: 6px;
}

.action-menu-bot-config__bot-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-menu-bot-config__add-button {
  width: calc(100% - 36px);
  margin: 2px 18px 0;
}

/* Edit/import page: the deepest "turn it over in your hands" surface. Its own
   warm-stone page, lifted above the darkened scene. */
.action-menu-bot-config__form {
  max-height: min(80dvh, 760px);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  color: var(--manuscript-ink);
  background: linear-gradient(180deg,
    color-mix(in srgb, var(--manuscript-page) 96%, white 4%),
    var(--manuscript-page));
  border: 1px solid var(--manuscript-page-line);
}

/* BaseContainer's header row wraps our slot in .header-content, which otherwise
   shrink-wraps; let it span so the manage cluster reaches the far right. */
.action-menu-bot-config__form :deep(.header-content) {
  flex: 1;
  min-width: 0;
}

.action-menu-bot-config__form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.action-menu-bot-config__form-title {
  margin: 0;
  font: 400 1.5rem/1.15 var(--manuscript-serif);
  color: var(--manuscript-ink);
}

/* Recessive management cluster — present when editing, quiet enough to stay out
   of the primary Update/Cancel flow. */
.action-menu-bot-config__form-manage {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.action-menu-bot-config__form-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  background: color-mix(in srgb, black 68%, transparent);
  overscroll-behavior: contain;
  touch-action: none;
}

.action-menu-bot-config__import-error {
  margin-top: 8px;
  color: var(--theme-danger);
  font-size: 0.875rem;
}

.action-menu-bot-config__selection-error {
  margin: 0 18px;
  color: var(--theme-danger);
  font-size: .875rem;
}

/* Delete confirmation must sit above the edit form that opened it. */
.action-menu-bot-config__confirmation {
  z-index: 1001;
}

@media (max-width: 768px) {
  .action-menu-bot-config__form-overlay {
    align-items: flex-end;
    padding: 8px;
    padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
  }

  .action-menu-bot-config__form {
    max-height: min(88dvh, 100%);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}
</style>
